import "./Gallery.scss";

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import getImageUrl from "../../../Utils/Services/img/imgFetch";

const AUTO_PLAY_MS = 10000;
const FADE_MS = 450;

const Gallery: React.FC = () => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement | null>(null);

  type GalleryItem = { type: "image" | "video"; src: string; caption: string };
  const images: GalleryItem[] = [
    {
      type: "image",
      src: getImageUrl("gallery/bienveillance.JPG"),
      caption: "Bienveillance",
    },
    {
      type: "image",
      src: getImageUrl("gallery/constriction 2.JPG"),
      caption: "Construction",
    },
    {
      type: "image",
      src: getImageUrl("gallery/construction 3.JPG"),
      caption: "Construction",
    },
    {
      type: "image",
      src: getImageUrl("gallery/ecole.jpg"),
      caption: "École",
    },
    {
      type: "image",
      src: getImageUrl("gallery/ecole2.jpg"),
      caption: "École",
    },
    {
      type: "image",
      src: getImageUrl("gallery/ecole3.JPG"),
      caption: "École",
    },
    {
      type: "image",
      src: getImageUrl("gallery/inovation.JPG"),
      caption: "Innovation",
    },
    {
      type: "image",
      src: getImageUrl("gallery/inovation (2).JPG"),
      caption: "Innovation",
    },
    {
      type: "image",
      src: getImageUrl("gallery/promenade.JPG"),
      caption: "Promenade",
    },
    {
      type: "image",
      src: getImageUrl("gallery/promenade 2.JPG"),
      caption: "Promenade",
    },
    {
      type: "image",
      src: getImageUrl("gallery/promenade1.JPG"),
      caption: "Promenade",
    },
    {
      type: "image",
      src: getImageUrl("gallery/soudure.jpg"),
      caption: "Soudure",
    },
    {
      type: "image",
      src: getImageUrl("gallery/concours2008.JPG"),
      caption: "Concours 2008",
    },
    {
      type: "image",
      src: getImageUrl("gallery/transportdemarchandise.jpg"),
      caption: "Transport de marchandise",
    },
    {
      type: "image",
      src: getImageUrl("gallery/promenadeRavel.JPG"),
      caption: "Promenade Ravel",
    },
    {
      type: "image",
      src: getImageUrl("gallery/sensibilation-au-tri.jpg"),
      caption: "Sensibilation au tri",
    },
    {
      type: "image",
      src: getImageUrl("gallery/premier-prototype.jpg"),
      caption: "Premier prototype",
    },
    {
      type: "image",
      src: getImageUrl("gallery/Waterloo-001.jpg"),
      caption: "Waterloo 2006",
    },
    {
      type: "image",
      src: getImageUrl("gallery/Waterloo-002.jpg"),
      caption: "Waterloo 2006",
    },
    {
      type: "image",
      src: getImageUrl("gallery/Waterloo-003.jpg"),
      caption: "Waterloo 2006",
    },
    {
      type: "video",
      src: getImageUrl("gallery/promenade.mp4"),
      caption: "Promenade",
    },
  ];

  const [index, setIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Preload images to avoid flashes (ignore videos)
  useEffect(() => {
    images
      .filter((i) => i.type === "image")
      .forEach(({ src }) => {
        const img = new Image();
        img.src = src;
      });
  }, [images]);

  const goTo = (i: number) => {
    const nextIndex = (i + images.length) % images.length;
    if (nextIndex === index || isTransitioning) return;
    setPrevIndex(index);
    setIsTransitioning(true);
    setIndex(nextIndex);
    window.setTimeout(() => setIsTransitioning(false), FADE_MS);
  };
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  useEffect(() => {
    const isOnVideoAndPlaying =
      images[index]?.type === "video" && isVideoPlaying === true;
    if (isOnVideoAndPlaying) return;
    const id = window.setInterval(() => {
      if (!isTransitioning) next();
    }, AUTO_PLAY_MS);
    return () => window.clearInterval(id);
  }, [index, isTransitioning, isVideoPlaying, images]);

  return (
    <div className="gallerySectionDiv" id="gallery">
      <section className="gallerySection" ref={sectionRef}>
        <h2>{t("home.gallery.title")}</h2>
        <div
          className={
            "galleryViewport" + (isTransitioning ? " transitioning" : "")
          }
        >
          <button
            aria-label={t("home.gallery.prev")}
            className="nav prev"
            onClick={prev}
            onMouseDown={(e) => e.preventDefault()}
          >
            ‹
          </button>

          {prevIndex !== null &&
            isTransitioning &&
            images[prevIndex] &&
            (images[prevIndex].type === "image" ? (
              <img
                key={`prev-${prevIndex}`}
                src={images[prevIndex].src}
                alt={images[prevIndex].caption}
                className="slide prev"
              />
            ) : (
              <video
                key={`prev-${prevIndex}`}
                src={images[prevIndex].src}
                className="slide prev"
                muted
                playsInline
                preload="metadata"
              />
            ))}

          {images[index] &&
            (images[index].type === "image" ? (
              <img
                key={`curr-${index}`}
                src={images[index].src}
                alt={images[index].caption}
                className="slide current"
              />
            ) : (
              <video
                key={`curr-${index}`}
                src={images[index].src}
                className="slide current"
                controls
                muted
                playsInline
                preload="metadata"
                ref={videoRef}
                onPlay={() => setIsVideoPlaying(true)}
                onPause={() => setIsVideoPlaying(false)}
                onEnded={() => setIsVideoPlaying(false)}
              />
            ))}

          <button
            aria-label={t("home.gallery.next")}
            className="nav next"
            onClick={next}
            onMouseDown={(e) => e.preventDefault()}
          >
            ›
          </button>
        </div>
        {images[index] && <p className="caption">{images[index].caption}</p>}
        <div className="dots">
          {images.map((_, i) => (
            <button
              key={i}
              className={"dot" + (i === index ? " active" : "")}
              aria-label={`${t("home.gallery.goTo")} ${i + 1}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default Gallery;
